// React
import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
// MUI
import { Grid, Box, Button, Container, Divider } from "@mui/material";
import Iconify from "../../../components/iconify/Iconify";
// Constants
import EntityConstants from "../../../configs/EntityConstants";
import MessageContants from "../../../configs/MessageContants";
// Service
import { get, post, put, gen_link } from "../../../services/FetchHelper";
import SpinService from "../../../services/SpinService";
// Utils
import { DEBIT_NOTE_STATE, DEBIT_NOTE_SCHEMA, DEBIT_NOTE_FORM_FIELDS } from "./DebitNote.utils";
// Styles
import { StyledDrawerForm } from "../../../configs/GoStyles";
//Third party
import { Form, Formik } from "formik";
import toast from "react-hot-toast";
import GoForm from "../../../components/Inputs/GoForm";
import PermissionConstants from "../../../configs/PermissionConstants";
import usePermissions from "../../../hooks/usePermissions";

import DebitNoteLines from "./DebitNoteLines";

const DebitNote = ({ id, onClose }) => {
    const permissions = usePermissions();
    const spinService = SpinService();
    const [debitNote, setDebitNote] = useState(DEBIT_NOTE_STATE);
    const [schema, setSchema] = useState(DEBIT_NOTE_SCHEMA);
    const [formFields, setFormFields] = useState(DEBIT_NOTE_FORM_FIELDS);
    const [debitNoteLines, setDebitNoteLines] = useState([]);
    const [debitNoteId, setDebitNoteId] = useState(id);

    const attachOptions = (name, options) => {
        setFormFields(prevFormFields =>
            prevFormFields.map(field =>
                field.name === name && (field.type === "select" || field.type === "autocomplete")
                    ? { ...field, options: options }
                    : field
            )
        );
    };

    const handleAccountChange = async () => {
        spinService.on();
        const response = await get(EntityConstants.ACCOUNTS, "max=all");
        const data = await response.json();
        attachOptions("debit_ledger_id", data);
        spinService.off();
    };

    const handleDebitNoteChange = async (debitNoteId) => {
        spinService.on();
        const response = await get(EntityConstants.DEBIT_NOTES + `/${debitNoteId}`);
        const data = await response.json();
        setDebitNote(data);
        spinService.off();
        setDebitNoteLines(data.debit_note_lines);
    };

    const handleSubmit = async (values) => {
        spinService.on();
        const isNewDebitNote = values.id === undefined;
        const url = isNewDebitNote ? EntityConstants.DEBIT_NOTES : EntityConstants.DEBIT_NOTES + `/${values.id}`;
        const method = isNewDebitNote ? post : put;
        const response = await method(url, { debit_note: values });
        if ([200, 201].indexOf(response.status) > -1) {
            const data = await response.json();
            toast.success(isNewDebitNote ? MessageContants.DEBIT_NOTE_CREATED : MessageContants.DEBIT_NOTE_UPDATED);
            handleDebitNoteChange(data.id);
            setDebitNoteId(data.id);
            setDebitNoteLines(data.debit_note_lines);
        }
        spinService.off();
    };

    const handlePdfDownloadChange = async () => {
        const response = await fetch(gen_link(`${EntityConstants.DEBIT_NOTE_PDF_DOWNLOAD}/${debitNoteId}`));
        const pdfBlob = await response.blob();
        const pdfBlobUrl = URL.createObjectURL(pdfBlob);
        const previewWindow = window.open(pdfBlobUrl, '_blank');

        // const link = document.createElement('a');
        // link.href = gen_link(`${EntityConstants.DEBIT_NOTE_PDF_DOWNLOAD}/${debitNoteId}`);
        // link.target = '_blank';
        // link.click();
    };

    useEffect(() => {
        handleAccountChange();
        if (id !== undefined) {
            setDebitNoteId(Number(id))
            handleDebitNoteChange(Number(id));
        }
        return () => {
            setDebitNote({});
        }
    }, [id]);

    return (
        <Container>
            <Formik
                enableReinitialize
                initialValues={debitNote}
                validationSchema={schema}
                onSubmit={(values) => handleSubmit(values)}
            >
                {({
                    values,
                    touched,
                    errors,
                    handleChange,
                    handleSubmit,
                }) => (
                    <Form noValidate onSubmit={handleSubmit}>
                        <StyledDrawerForm>
                            <Grid
                                container
                                // maxWidth="md"
                                rowSpacing={1}
                                columnSpacing={{ xs: 1, sm: 2, md: 2 }}
                            >
                                {formFields.map((formField, index) => {
                                    return (
                                        <Grid key={index} item xs={12} sm={12} md={6} lg={6}>
                                            <GoForm
                                                config={formField}
                                                values={values}
                                                touched={touched}
                                                errors={errors}
                                                handleChange={(e) => { handleChange(e) }}
                                            />
                                        </Grid>
                                    );
                                })}
                            </Grid>
                            <Box sx={{ p: 2, textAlign: "right" }}>
                                <Button
                                    variant="contained"
                                    color="error"
                                    startIcon={<Iconify icon="eva:close-circle-outline" />}
                                    onClick={() => onClose()}
                                >
                                    Close
                                </Button>
                                &nbsp;&nbsp;
                                {((values.id === undefined && permissions.has(PermissionConstants.DEBIT_NOTE_CREATE)) || (values.id !== undefined && permissions.has(PermissionConstants.DEBIT_NOTE_UPDATE))) &&
                                    <Button
                                        variant="contained"
                                        startIcon={<Iconify icon="eva:save-fill" />}
                                        type="submit"
                                    >
                                        {values.id === undefined ? 'Submit' : 'Update'}
                                    </Button>
                                }
                                &nbsp;&nbsp;
                                {(values.id !== undefined && permissions.has(PermissionConstants.DEBIT_NOTE_DOWNLOAD)) &&
                                    <Button
                                        variant="contained"
                                        startIcon={<Iconify icon="typcn:download" />}
                                        onClick={handlePdfDownloadChange}
                                    >
                                        Download
                                    </Button>
                                }
                            </Box>
                        </StyledDrawerForm>
                    </Form>
                )}
            </Formik>
            <Divider />
            <br />
            {debitNoteId !== undefined && <DebitNoteLines debitNoteId={debitNoteId} debitNoteLines={debitNoteLines} reload={() => handleDebitNoteChange(debitNoteId)} />}
        </Container>
    );
};

DebitNote.propTypes = {
    id: PropTypes.number,
    onClose: PropTypes.any,
};

export default DebitNote;